import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleQuestion, faMagnifyingGlassPlay } from '@/fontawesome';
import { useVideoFinder } from '@/contexts/VideoFinderContext';
import { useUser } from '@/contexts/UserContext';
import { hasActiveRecurringSubscription } from '@/utils/subscription-status';
import AskQuestionModal from '@/components/questions/AskQuestionModal';

// Seiten, die unten rechts schon selbst etwas anzeigen:
// - /chats: eigener "Neuer Chat"-FAB
// - /chats/:id: Senden-Button der Chat-Eingabe
const HIDDEN_PATH_PREFIXES = ['/chats'];

// Auf dem Dashboard liegt der BottomMarketTicker (fixed bottom-0, ca. 56px hoch) im Weg
const DASHBOARD_PATH = '/dashboard';

const FAB_CLASS = 'bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer';

interface MenuItem {
  key: string;
  label: string;
  icon: IconDefinition;
  onClick: () => void;
}

/**
 * Schwebendes Aktionsmenü unten rechts auf allen eingeloggten Seiten.
 * Ein Eintrag wird direkt als Button gezeigt, mehrere hinter einem Menü-Button ausgeklappt.
 * Wird in PersistentLayout gerendert und regelt seine Sichtbarkeit selbst.
 */
export default function FloatingActionMenu() {
  const { isAvailable, isOpen, open } = useVideoFinder();
  const { user } = useUser();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [askOpen, setAskOpen] = useState(false);

  useEffect(() => {
    setExpanded(false);
    setHovered(false);
  }, [location.pathname]);

  const modal = <AskQuestionModal isOpen={askOpen} onClose={() => setAskOpen(false)} />;

  // Bei offenem Finder oder Frage-Modal ausblenden, sonst schimmert der Button durch den Backdrop
  if (isOpen || askOpen) {
    return modal;
  }

  const isHidden = HIDDEN_PATH_PREFIXES.some(
    (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  );
  if (isHidden) {
    return modal;
  }

  const items: MenuItem[] = [];
  if (isAvailable) {
    items.push({ key: 'finder', label: 'Video-Finder', icon: faMagnifyingGlassPlay, onClick: open });
  }
  if (user !== null && hasActiveRecurringSubscription(user) && !user.featureConfig.communityMember) {
    items.push({ key: 'question', label: 'Frage stellen', icon: faCircleQuestion, onClick: () => setAskOpen(true) });
  }
  if (items.length === 0) {
    return modal;
  }

  const bottomClass = location.pathname === DASHBOARD_PATH
    ? 'bottom-[calc(4.5rem+env(safe-area-inset-bottom))]'
    : 'bottom-[max(1.5rem,env(safe-area-inset-bottom))]';

  if (items.length === 1) {
    const item = items[0];
    return (
      <>
        {modal}
        <button
          type="button"
          onClick={item.onClick}
          title={item.label}
          aria-label={item.label}
          className={`fixed ${bottomClass} right-6 z-50 group ${FAB_CLASS}`}
        >
          <div className="flex items-center px-4 py-4">
            <FontAwesomeIcon icon={item.icon} className="w-6 h-6 flex-shrink-0" />
            <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out">
              <span className="pl-3 whitespace-nowrap">{item.label}</span>
            </span>
          </div>
        </button>
      </>
    );
  }

  // Hover öffnet nur über den Menü-Button und schließt erst beim Verlassen des Containers.
  // Geschlossen ist der Container pointer-events-none, sonst blockiert seine Fläche die Seite darunter.
  const isMenuOpen = expanded || hovered;
  const itemClass = isMenuOpen
    ? 'opacity-100 translate-y-0 scale-100'
    : 'opacity-0 translate-y-2 scale-95';
  // Hamburger-Morph: mittlerer Balken blendet aus, oberer und unterer drehen sich zum X.
  // Alle drei Balken sitzen vertikal mittig (top 11px bei 24px Box), die Ruhelage kommt über translate.
  const barBase = 'absolute left-0 top-[11px] block w-6 h-0.5 rounded-full bg-current transition-all duration-300 ease-in-out';
  const topBarClass = isMenuOpen ? 'translate-y-0 rotate-45' : '-translate-y-[7px] rotate-0';
  const midBarClass = isMenuOpen ? 'opacity-0 scale-x-0' : 'opacity-100 scale-x-100';
  const bottomBarClass = isMenuOpen ? 'translate-y-0 -rotate-45' : 'translate-y-[7px] rotate-0';

  return (
    <>
      {modal}
      {expanded && (
        <div className="fixed inset-0 z-40" onClick={() => setExpanded(false)} aria-hidden="true" />
      )}
      <div
        className={`fixed ${bottomClass} right-6 z-50 flex flex-col items-end gap-3 ${isMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovered(false); }}
      >
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            tabIndex={isMenuOpen ? 0 : -1}
            className={`flex items-center gap-3 px-4 py-3 transition-all duration-200 ease-out ${FAB_CLASS} ${itemClass}`}
            onClick={() => { setExpanded(false); setHovered(false); item.onClick(); }}
          >
            <FontAwesomeIcon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
            <span className="whitespace-nowrap">{item.label}</span>
          </button>
        ))}
        <button
          type="button"
          onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovered(true); }}
          onClick={() => setExpanded((prev) => !prev)}
          title="Aktionen"
          aria-label={expanded ? 'Aktionen schließen' : 'Aktionen öffnen'}
          aria-expanded={expanded}
          className={`p-4 pointer-events-auto ${FAB_CLASS}`}
        >
          <span className="relative block w-6 h-6" aria-hidden="true">
            <span className={`${barBase} ${topBarClass}`} />
            <span className={`${barBase} ${midBarClass}`} />
            <span className={`${barBase} ${bottomBarClass}`} />
          </span>
        </button>
      </div>
    </>
  );
}
